import React from "react";
import { Helmet } from "react-helmet-async";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { MdSearchOff } from "react-icons/md";

import styles from "./ItemDetails.module.scss";
import { useItemDetails } from "./useItemDetails";
import { selectClusterList } from "../../../store/slices/clusterListSlice";
import { PageHeader } from "../../../common/components/pageHeader/PageHeader";
import { EmptyState } from "../../../common/components/emptyState/EmptyState";
import {
  getClusterImageUrl,
  getFirstImage,
} from "../../../common/utils/functions/getImageUri";
import { clusterRoute } from "../../../common/utils/constants";
import { useCartActions } from "../../../common/hooks/useCartActions";
import { ItemCardButton } from "../../../common/components/itemCardButton/ItemCardButton";
import { FavoriteButton } from "../../../common/components/favoriteButton/FavoriteButton";

export const ItemDetails: React.FC = () => {
  const clusters = useSelector(selectClusterList);
  const { item, ready, displayPrice, hasParentPrice, children } =
    useItemDetails();

  const cart = useCartActions(item);

  if (!ready) return null;

  if (!item) {
    return (
      <div className={styles.Page}>
        <EmptyState
          icon={MdSearchOff}
          title="Товар не найден"
          description="Возможно, он был удален или скрыт."
        />
      </div>
    );
  }

  const cluster = clusters.find((c) => c.id === item.clusterId);
  const image = getFirstImage(item.files);
  const hasPrice = displayPrice > 0;
  const isFavorite = !!item.selected?.some((s) => s.isActive);
  const isOutOfStock =
    item.isAvailableInSelectedCity !== false &&
    item.isInStockInSelectedCity === false;

  const externalItemUrl = cluster
    ? `https://${cluster.domain}/item/${item.id}`
    : null;

  return (
    <div className={styles.Page}>
      <Helmet>
        <title>{`${item.name} — ЛОГМОЛЛ`}</title>
        <meta name="description" content={item.description || item.name} />
      </Helmet>

      <PageHeader title={item.name} subtitle={cluster?.name} />

      <div className={styles.Layout}>
        <div className={styles.ImageSide}>
          {image && cluster ? (
            <img
              src={getClusterImageUrl(cluster, image)}
              alt={item.name}
              className={styles.MainImage}
            />
          ) : (
            <div className={styles.ImagePlaceholder} />
          )}

          {isOutOfStock && (
            <span className={styles.OutOfStockImageBadge}>Нет в наличии</span>
          )}

          <div className={styles.FavoriteWrap}>
            <FavoriteButton
              itemId={item.id}
              isFavorite={isFavorite}
              size={24}
            />
          </div>
        </div>

        <div className={styles.InfoSide}>
          <h1 className={styles.Title}>{item.name}</h1>

          {cluster && (
            <Link to={clusterRoute(cluster.id)} className={styles.ClusterLink}>
              <img
                src={`https://${cluster.domain}/icons/logo512.png`}
                alt={cluster.name}
                className={styles.ClusterLogo}
              />
              <span className={styles.ClusterName}>{cluster.name}</span>
            </Link>
          )}

          {cluster?.cdek?.enabled && (
            <span className={styles.CdekBadge}>Доставка по РФ</span>
          )}

          {hasPrice && (
            <div className={styles.PriceBox}>
              <span className={styles.Price}>{displayPrice} ₽</span>
            </div>
          )}

          {isOutOfStock && (
            <span className={styles.OutOfStockBadge}>Нет в наличии</span>
          )}

          {item.description && (
            <div className={styles.DescriptionBox}>
              <h2 className={styles.SectionTitle}>Описание</h2>
              <p className={styles.Description}>{item.description}</p>
            </div>
          )}

          {children.length > 0 && (
            <div className={styles.DescriptionBox}>
              <h2 className={styles.SectionTitle}>Состав комплекта</h2>
              <ul className={styles.KitList}>
                {children.map((kit: any) => {
                  const child = kit.item || kit.childItem;
                  if (!child) return null;
                  const childPrice = child.prices?.[0]?.price;

                  return (
                    <li key={kit.childItemId} className={styles.KitItem}>
                      <span>{child.name}</span>
                      <span className={styles.KitQuantity}>
                        x{kit.quantity}
                      </span>

                      {!hasParentPrice && childPrice && (
                        <span className={styles.KitPrice}>{childPrice} ₽</span>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {externalItemUrl && cluster && (
            <a
              href={externalItemUrl}
              target="_blank"
              rel="noopener noreferrer nofollow"
              className={styles.ExternalLink}
            >
              Посмотреть товар в {cluster.name} →
            </a>
          )}

          {(cart.showCartButton || cart.showComplexButton) && (
            <div className={styles.ActionsPlaceholder}>
              <ItemCardButton
                quantity={cart.quantity}
                showCartButton={!!cart.showCartButton}
                showComplexButton={cart.showComplexButton}
                isComplexCategory={cart.isComplexCategory}
                isOutOfStock={isOutOfStock}
                isIncreaseDisabled={false}
                hasPrice={hasPrice}
                isInComplexCart={cart.isInComplexCart}
                onAddToCart={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (isOutOfStock) return;
                  cart.handleAddToCart();
                }}
                onAddToComplex={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (isOutOfStock) return;
                  cart.handleAddToComplex();
                }}
                onIncrease={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  cart.handleIncrease();
                }}
                onDecrease={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  cart.handleDecrease();
                }}
                onRemove={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  cart.handleRemove();
                }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};